import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

const Values = () => {
  const { t } = useTranslation();

  const values = [
    {
      letter: 'A',
      title: t('values.items.adapt.title'),
      subtitle: t('values.items.adapt.subtitle'),
      description: t('values.items.adapt.description'),
      color: 'bg-csl-green',
      delay: '0ms'
    },
    {
      letter: 'O',
      title: t('values.items.offer.title'),
      subtitle: t('values.items.offer.subtitle'),
      description: t('values.items.offer.description'),
      color: 'bg-blue-500',
      delay: '200ms'
    },
    {
      letter: 'B',
      title: t('values.items.build.title'),
      subtitle: t('values.items.build.subtitle'),
      description: t('values.items.build.description'),
      color: 'bg-green-600',
      delay: '400ms'
    },
    {
      letter: 'D',
      title: t('values.items.develop.title'),
      subtitle: t('values.items.develop.subtitle'),
      description: t('values.items.develop.description'),
      color: 'bg-orange-500',
      delay: '600ms'
    }
  ];

  return (
    <section id="values" className="py-12 sm:py-16 lg:py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-csl-green/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-blue-500/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 animate-slide-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            <Trans i18nKey="values.title">
              Nos Valeurs <span className="text-csl-green">Fondamentales</span>
            </Trans>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            {t('values.description')}
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {values.map((value, index) => (
            <div
              key={value.title}
              className="group relative bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 animate-slide-up hover:-translate-y-2"
              style={{ animationDelay: value.delay }}
            >
              {/* Large Letter Background */}
              <div className={`absolute top-3 sm:top-4 right-3 sm:right-4 text-6xl sm:text-8xl lg:text-9xl font-black opacity-5 group-hover:opacity-10 transition-opacity duration-300 select-none ${value.color.replace('bg-', 'text-')}`}>
                {value.letter}
              </div>

              {/* Icon/Letter Circle */}
              <div className={`w-12 h-12 sm:w-16 sm:h-16 ${value.color} rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-lg sm:text-2xl font-bold mb-4 sm:mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                {value.letter}
              </div>

              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className={`text-xs sm:text-sm font-semibold uppercase tracking-wider mb-3 sm:mb-4 ${value.color.replace('bg-', 'text-')}`}>
                  {value.subtitle}
                </p>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>

              {/* Bottom Decoration */}
              <div className={`absolute bottom-0 left-0 w-full h-1 ${value.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`} />
            </div>
          ))}
        </div>

        {/* Mission Statement */}
        <div className="bg-gradient-to-r from-csl-green to-csl-dark-green rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-white animate-slide-up shadow-xl">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">{t('values.mission.title')}</h3>
            <div className="w-16 sm:w-24 h-1 bg-white/30 mx-auto" />
          </div>

          <p className="text-base sm:text-lg leading-relaxed max-w-4xl mx-auto text-center mb-6 sm:mb-8 font-medium px-4">
            {t('values.mission.description')}
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 transition-transform duration-300 hover:scale-[1.02]">
            <h4 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{t('values.vision.title')}</h4>
            <p className="text-white/90 text-sm sm:text-base">
              {t('values.vision.description')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Values;